// DeviceDetailComponent.js
import { useState, useEffect } from "react";
import {
    EditableInputComponent,
    EditableSelectComponent,
    EditableTextareaComponent,
    SectionHeader,
} from "components";
import { Document, Eye, Plus } from "assets/icons";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { FileLabel, FileLog, PictureLabel } from "shared/types/file";
import ModalUploadFileLog, { FileUploadField } from "shared/components/uploadFileModal/uploadFileModal";
import { UnitDTO } from "shared/types/unit";
import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";
import ReactDatePicker from "react-datepicker";

// Type assertion for React 18 compatibility
const DatePicker = ReactDatePicker as any;

import { ClimatisationDevice, ClimatisationInfo, DeviceDrives, DeviceType, DistributionDeviceSubType, GeneratorDeviceSubtype, MeasurementDeviceSubType } from "shared/types/climatisation";
import { BuildingDTO } from "shared/types/building";
import ButtonNew from "components/buttonNew/buttonNew";
import { Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Tooltip } from "@heroui/react";
import { Info } from "@phosphor-icons/react";

interface DeviceDetailComponentProps {
    climatisationDevice: ClimatisationDevice;
    climatisationInfo?: ClimatisationInfo;
    handleUnitConformityInfoChangeInParent?: (value: any) => any;
    handleSaveInParent?: () => any;
    handleCloseParent?: () => any;
    parentEditMode?: boolean;
    unit?: UnitDTO;
    building?: BuildingDTO;
    fetchDevices?: (climatisationInfoId: any) => any;
}

const ClimatisationDeviceDetailComponent = ({
    climatisationDevice,
    climatisationInfo,
    handleCloseParent,
    handleSaveInParent,
    parentEditMode = true,
    unit,
    building,
    fetchDevices,
}: DeviceDetailComponentProps) => {
    const [editMode, setEditMode] = useState(parentEditMode);
    const [editedDeviceInfo, setEditedDeviceInfo] = useState<ClimatisationDevice | null>(climatisationDevice);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const { session } = useSession();
    const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
    const [selectedUnit, setSelectedUnit] = useState<UnitDTO | null>(unit as UnitDTO);
    const [selectedBuilding, setSelectedBuilding] = useState<BuildingDTO | null>(building as BuildingDTO);

    useEffect(() => {
        setSelectedUnit(unit as UnitDTO);
    }, [unit]);

    useEffect(() => {
        setSelectedBuilding(building as BuildingDTO);
    }, [building]);

    useEffect(() => {
        setEditedDeviceInfo(climatisationDevice);
    }, []);

    const handleSave = async () => {
        console.log(editedDeviceInfo);

        if (editedDeviceInfo && !editedDeviceInfo.climatisationInfo) {
            editedDeviceInfo.climatisationInfo = climatisationInfo;
        }

        if (building) {
            (editedDeviceInfo as any).buildingId = building?.id;
        }

        if (unit) {
            (editedDeviceInfo as any).unitId = unit?.id;
        }

        if (!editedDeviceInfo?.deviceType) {
            return toast.warning("Toesteltype is verplicht");
        }

        if (!editedDeviceInfo?.deviceSubType) {
            return toast.warning("Toestel subtype is verplicht");
        }

        if (!editedDeviceInfo?.deviceLocation) {
            return toast.warning("De locatie van het toestel is verplicht");
        }

        console.log(editedDeviceInfo);


        setHasUnsavedChanges(false);
        try {
            if (handleSaveInParent) {
                handleSaveInParent();
            } else {
                let res = await fetchWithAuth(
                    `${API_URL}/climatisation/device`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(editedDeviceInfo),
                    },
                    session,
                );
                const device: ClimatisationDevice = await res.json();
                if (res.ok) {
                    setEditedDeviceInfo(device);
                    fetchDevices && fetchDevices(device.climatisationInfo && device.climatisationInfo.id);
                    toast.success("Informatie van toestel succesvol bijgewerkt!");
                } else {
                    toast.warning("Er is iets misgegaan!");
                }
            }
        } catch (e) {
            console.error(e);
            toast.error(
                "Er is een fout opgetreden bij het bijwerken van de toestel informatie.",
            );
        }
    };

    const updateDeviceInfo = (updatedFields: any) => {
        setHasUnsavedChanges(true);
        setEditedDeviceInfo((prev: any) => ({ ...prev, ...updatedFields }));
    };

    const handleOpenAddDevicedocumentModal = () => {
        if (!canUpload()) {
            return toast.warning(`Vul eerst de verplichte velden in: 'Toestel Type", 'Toestel subtype' en 'Toestel locatie'`);
        }
        setFileModalOpen(true);
    }

    const handleFileModalClose = () => {
        setFileModalOpen(false);
    }

    const handleFileModalSave = async (fileLogData: any) => {
        if (editedDeviceInfo) {
            const fileLogs = [...(editedDeviceInfo.groupedFileLogs || []), fileLogData.fileLog];

            // Update the device info with the new file logs
            const updatedDeviceInfo: ClimatisationDevice = {
                ...editedDeviceInfo,
                groupedFileLogs: fileLogs
            };

            // If editedDeviceInfo was potentially partial, ensure defaults or existing values are used
            setEditedDeviceInfo(updatedDeviceInfo as ClimatisationDevice);
            fetchDevices && fetchDevices(updatedDeviceInfo.climatisationInfo?.id);
            handleFileModalClose();
        }
    };

    const canUpload = () => {
        return editedDeviceInfo && editedDeviceInfo.deviceType && editedDeviceInfo.deviceSubType && editedDeviceInfo.deviceLocation;
    }

    const handleFileUploadSuccess = (fileLogAndCustomData: any) => {
        console.log(fileLogAndCustomData)
        const { savedClimatisationDeviceInfo }: { savedClimatisationDeviceInfo: ClimatisationDevice } = fileLogAndCustomData.customData;

        console.log("Saved climatisation device info");
        console.log(savedClimatisationDeviceInfo);

        if(savedClimatisationDeviceInfo.groupedFileLogs && savedClimatisationDeviceInfo.groupedFileLogs.length > (editedDeviceInfo?.groupedFileLogs?.length || [].length)) {
            setEditedDeviceInfo((prev: any) => ({ ...prev, groupedFileLogs: savedClimatisationDeviceInfo.groupedFileLogs }));
        }

        if(savedClimatisationDeviceInfo.pictureFileLogIds && savedClimatisationDeviceInfo.pictureFileLogIds.length > (editedDeviceInfo?.pictureFileLogIds.length || [].length)) {
            setEditedDeviceInfo((prev: any) => ({ ...prev, pictureFileLogIds: savedClimatisationDeviceInfo.pictureFileLogIds, pictureFileLogs: savedClimatisationDeviceInfo.pictureFileLogs}));
        }

        if(savedClimatisationDeviceInfo.maintenanceAttestFileLogIds && savedClimatisationDeviceInfo.maintenanceAttestFileLogIds.length > (editedDeviceInfo?.maintenanceAttestFileLogIds.length || [].length)) {
            setEditedDeviceInfo((prev: any) => ({ ...prev, maintenanceAttestFileLogIds: savedClimatisationDeviceInfo.maintenanceAttestFileLogIds, maintenanceAttestFileLogs: savedClimatisationDeviceInfo.maintenanceAttestFileLogs }));
        }

        if(savedClimatisationDeviceInfo.acquisitionOrRepairinvoicesFileLogIds && savedClimatisationDeviceInfo.acquisitionOrRepairinvoicesFileLogIds.length > (editedDeviceInfo?.acquisitionOrRepairinvoicesFileLogIds.length || [].length)) {
            setEditedDeviceInfo((prev: any) => ({ ...prev, acquisitionOrRepairinvoicesFileLogIds: savedClimatisationDeviceInfo.acquisitionOrRepairinvoicesFileLogIds, acquisitionOrRepairinvoicesFileLogs: savedClimatisationDeviceInfo.acquisitionOrRepairinvoicesFileLogs }));
        }

        if(savedClimatisationDeviceInfo.manualFileLogIds && savedClimatisationDeviceInfo.manualFileLogIds.length > (editedDeviceInfo?.manualFileLogIds.length || [].length)) {
            setEditedDeviceInfo((prev: any) => ({ ...prev, manualFileLogIds: savedClimatisationDeviceInfo.manualFileLogIds, manualFileLogs: savedClimatisationDeviceInfo.manualFileLogs }));
        }
    }


    return (
        <section className="flex flex-col gap-0 rounded-md bg-white">
            <SectionHeader
                title="Toestel detail"
                icon={<Document />}
                editMode={editMode}
                sticky={false}
                onEditClick={() => setEditMode(!editMode)}
                onSaveClick={handleSave}
                showEditToggle={true}
                hasUnsavedChanges={hasUnsavedChanges}
                onCloseClick={() => {
                    if (handleCloseParent) handleCloseParent();
                }}
            />
            <div className="p-3">
                <div className="gap-3 sm:grid sm:grid-cols-2">
                    {/* Toesteltype */}
                    <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <p>Toestel type?</p> <Tooltip content="Dit veld moet ingevuld zijn om foto's en documenten te kunnen opladen"><Info size={20} weight="duotone" /></Tooltip>
                        </header>
                        <EditableSelectComponent
                            label="Maak een keuze"
                            value={(editedDeviceInfo && editedDeviceInfo?.deviceType) || ""}
                            isLoading={false}
                            options={Object.values(DeviceType).map((value) => ({
                                label: value,
                                value,
                            }))}
                            editMode={editMode}
                            onChange={(value) => updateDeviceInfo({ deviceType: value })}
                        />
                    </div>
                    {/* Toestelsubtype */}
                    <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <p>Toestelsubtype?</p> <Tooltip content="Dit veld moet ingevuld zijn om foto's en documenten te kunnen opladen"><Info size={20} weight="duotone" /></Tooltip>
                        </header>
                        <EditableSelectComponent
                            label={!editedDeviceInfo?.deviceType ? "Vul eerst het toesteltype in" : "Maak een keuze"}
                            value={(editedDeviceInfo && editedDeviceInfo?.deviceSubType) || ""}
                            isLoading={false}
                            options={Object.values(
                                editedDeviceInfo?.deviceType === DeviceType.DISTRIBUTION ? DistributionDeviceSubType :
                                    editedDeviceInfo?.deviceType === DeviceType.GENERATOR ? GeneratorDeviceSubtype :
                                        editedDeviceInfo?.deviceType === DeviceType.MEASUREMENT ? MeasurementDeviceSubType : {}
                            ).map(value => ({
                                label: value,
                                value
                            })).sort((a, b) => a.label.localeCompare(b.label))}
                            isDisabled={!editedDeviceInfo?.deviceType}
                            editMode={editMode}
                            onChange={(value) => updateDeviceInfo({ deviceSubType: value })}
                        />

                    </div>
                    {/* Drijft aan */}
                    {editedDeviceInfo && editedDeviceInfo.deviceType == DeviceType.GENERATOR && <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <p>Wat drijft dit aan?</p>
                        </header>
                        <EditableSelectComponent
                            label={"Maak een keuze"}
                            value={(editedDeviceInfo && editedDeviceInfo?.drives) || ""}
                            isLoading={false}
                            options={Object.values(
                                DeviceDrives
                            ).map(value => ({
                                label: value,
                                value
                            })).sort((a, b) => a.label.localeCompare(b.label))}
                            editMode={editMode}
                            onChange={(value) => updateDeviceInfo({ drives: value })}
                        />
                    </div>}

                     {/* Drives spaces */}
                     {editedDeviceInfo && editedDeviceInfo.deviceType == DeviceType.GENERATOR && editedDeviceInfo.drives && editedDeviceInfo.drives != DeviceDrives.UNKNOWN && <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <p>In welke ruimtes?</p> <Tooltip content="Vb. Soms is er een aparte doorstromer alleen voor het SWW in de badkamer"><Info size={20} weight="duotone" /></Tooltip>
                        </header>
                        <EditableTextareaComponent
                            label="In welke ruimtes is dit de voornaamste verwarmingsbron?"
                            value={(editedDeviceInfo && editedDeviceInfo?.drivesWhichRooms) || ""}
                            isLoading={false}
                            editMode={editMode}
                            onChange={(value) => updateDeviceInfo({ drivesWhichRooms: value })}
                        />
                    </div>}

                    {/* Location */}
                    <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <p>In welke ruimte staat het toestel?</p> <Tooltip content="Dit veld moet ingevuld zijn om foto's en documenten te kunnen opladen"><Info size={20} weight="duotone" /></Tooltip>
                        </header>
                        <EditableInputComponent
                            label="Locatie"
                            value={(editedDeviceInfo?.deviceLocation as any) || ""}
                            editMode={editMode}
                            isLoading={false}
                            onChange={(value) => updateDeviceInfo({ deviceLocation: value })}
                        />
                    </div>
                    {/* Merk */}
                    <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <p>Merk</p>
                        </header>
                        <EditableInputComponent
                            label="Merk"
                            value={(editedDeviceInfo?.brand as any) || ""}
                            editMode={editMode}
                            isLoading={false}
                            onChange={(value) => updateDeviceInfo({ brand: value })}
                        />
                    </div>
                    {/* Model */}
                    <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <p>Model</p>
                        </header>
                        <EditableInputComponent
                            label="Model"
                            value={(editedDeviceInfo?.model as any) || ""}
                            editMode={editMode}
                            isLoading={false}
                            onChange={(value) => updateDeviceInfo({ model: value })}
                        />
                    </div>
                    {/* Serial Number */}
                    <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <p>Serienummer</p>
                        </header>
                        <EditableInputComponent
                            label="Serienummer"
                            value={(editedDeviceInfo?.serialNumber as any) || ""}
                            editMode={editMode}
                            isLoading={false}
                            onChange={(value) => updateDeviceInfo({ serialNumber: value })}
                        />
                    </div>
                    <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <p>Installatiedatum toestel</p>
                        </header>
                        <DatePicker
                            selected={
                                editedDeviceInfo?.installationDate
                                    ? new Date(editedDeviceInfo?.installationDate.toString())
                                    : null
                            }
                            onChange={(date: any) => updateDeviceInfo({ installationDate: date })}
                            dateFormat="dd/MM/yyyy"
                            className="w-full rounded-sm bg-white p-2.5 text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
                        />
                    </div>

                    {/* Description */}
                    <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <p>Toestel beschrijving</p>
                        </header>
                        <EditableTextareaComponent
                            label="Toestel beschrijving"
                            value={(editedDeviceInfo && editedDeviceInfo?.remarks) || ""}
                            isLoading={false}
                            editMode={editMode}
                            onChange={(value) => updateDeviceInfo({ remarks: value })}
                        />
                    </div>

                    {/* Foto's */}
                    {canUpload() && (
                        <ImagesContainer
                            onImageUploadSuccess={(fileLogAndCustomData: any) => {
                                console.log("Image upload success");
                                console.log(fileLogAndCustomData);
                                setHasUnsavedChanges(false);
                                setEditedDeviceInfo(fileLogAndCustomData.customData.savedClimatisationDeviceInfo);
                                fetchDevices && fetchDevices(fileLogAndCustomData.customData.savedClimatisationDeviceInfo.climatisationInfo?.id);
                            }}
                            fetchOnlyPredefinedFileLogIds={true}
                            customData={{
                                climatisationDevice: editedDeviceInfo
                            }}
                            skipFields={(unit && [FileUploadField.BUILDING]) || (!unit && [FileUploadField.UNIT]) || []}
                            fileLogIds={editedDeviceInfo?.pictureFileLogIds || []}
                            title="Foto's toestel"
                            buildingId={building?.id as string}
                            unitId={unit?.id}
                            fileLabel={(building?.id && !unit?.id && PictureLabel.PictureCollectiveCentralHeatingBuilding) || (unit?.id && PictureLabel.PictureClimatisationDevice as any)}
                        />
                    )}

                    <div className="col-span-full mt-8 flex flex-col gap-3">
                        <header className="flex justify-between px-2.5">
                            <div className="flex items-center gap-2">
                                <Eye className="h-[18px] w-[18px]" />
                                <p className="text-lg">Documenten</p>
                            </div>
                            {<ButtonNew
                                onClick={handleOpenAddDevicedocumentModal}
                                icon={<Plus />}
                                label="Voeg document toe"
                                variant="primaryMoss"
                            />}
                        </header>
                        <Table>
                            <TableHeader>
                                <TableColumn>Label</TableColumn>
                                <TableColumn>Samenvatting</TableColumn>
                                <TableColumn>Documentdatum</TableColumn>
                                <TableColumn>Vervaldatum</TableColumn>
                                <TableColumn>Sharepoint</TableColumn>
                                {/* Other columns as needed */}
                            </TableHeader>
                            <TableBody>
                                {editedDeviceInfo && editedDeviceInfo.groupedFileLogs &&
                                    (editedDeviceInfo as any).groupedFileLogs.map((fileLog: FileLog) => (
                                        <TableRow key={fileLog.id}>
                                            <TableCell>
                                                {(fileLog.label &&
                                                    fileLog.label.length > 0 &&
                                                    fileLog.label[0]) ||
                                                    ""}
                                            </TableCell>
                                            <TableCell>{fileLog.summary}</TableCell>
                                            <TableCell>
                                                {fileLog?.documentDate
                                                    ? (fileLog?.documentDate as any)
                                                    : "Datum niet ingevuld"}
                                            </TableCell>
                                            <TableCell>
                                                {fileLog?.expirationDate
                                                    ? (fileLog?.expirationDate as any)
                                                    : "Vervaldatum niet ingevuld"}
                                            </TableCell>
                                            <TableCell>
                                                {fileLog ? (
                                                    <a
                                                        href={fileLog.sharePointFileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <Eye />
                                                    </a>
                                                ) : (
                                                    "No Document"
                                                )}
                                            </TableCell>
                                            {/* Other cells as needed */}
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </div>
                    {/* Modal for File Upload */}
                    <ModalUploadFileLog
                        isOpen={fileModalOpen}
                        onClose={() => setFileModalOpen(false)}
                        onSave={(fileLogData) => handleFileModalSave(fileLogData)}
                        building={selectedBuilding}
                        unit={selectedUnit}
                        onFileUploadSuccess={(uploadSuccessResult) => handleFileUploadSuccess(uploadSuccessResult)}
                        skipFields={(unit && [FileUploadField.BUILDING]) || (!unit && [FileUploadField.UNIT]) || []}
                        incomingCustomData={{
                            climatisationDevice: editedDeviceInfo
                        }}
                        customLabels={building && !unit && [
                            FileLabel.CollectieveVerwarmingKeuringsattest,
                            FileLabel.CollectieveVerwarmingOnderhoudsattest,
                            FileLabel.CollectieveVerwarmingToestelfiche,
                            FileLabel.CollectieveVerwarmingFactuur,
                        ] || unit && [
                            FileLabel.VerwarmingOnderhoudsattest,
                            FileLabel.VerwarmingKeuringsattest,
                            FileLabel.ToestelHandleiding,
                            FileLabel.ToestelFactuur,
                            FileLabel.ToestelFiche,
                        ]}
                    />
                </div>
            </div>
        </section >
    );
};

export default ClimatisationDeviceDetailComponent;
